import { Avatar, Box, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import RIB from '/@/components/refuge/refuge-options/assets/corn-AR-05.png';
import colors from '/@/components/theme-provider/colors.js';
import useUnitProvider from '/@/core/context/unit';
import { useCatalogProductsByBrands, useCornTechnologies } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import { useCatalogZones } from '/@/store/catalog/apiProviders';
import { getCatalogBrandsByCrop } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Brand, Technology as TechnologyType } from '/@/types/catalog';
import { reverse } from '/@/utils/router';
import { ProductSelectorButton } from '/@/views/product-exploration/components/list-sidebar';
import { parseProductForProductCard } from '../components/product-card';
import { ProductCard } from '../components/product-comparator';
import Structured1 from './assets/a-traves.svg';
import Structured2 from './assets/cabecera-circular.svg';
import Structured3 from './assets/esquina.svg';
import Structured5 from './assets/otro-campo.svg';
import Structured4 from './assets/pivot.svg';
import Structured6 from './assets/rodas-cabeceras.svg';

const Technology = () => {
    //Hooks
    const navigate = useNavigate();
    const technologiesQuery = useCornTechnologies({ page: 1, pageSize: 10000 });
    const cornCatalogBrands = useSelector((state) => getCatalogBrandsByCrop(state, 'corn'));
    const country = useSelector((state) => getSessionCountry(state));
    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, crop: 'corn', type: 'catalog' });
    const units = useUnitProvider();
    const allProducts = useCatalogProductsByBrands(
        cornCatalogBrands,
        {
            crop: 'corn',
            country: country?.code || 'AR',
            page: 1,
            pageSize: 10000,
        },
        { enabled: cornCatalogBrands.length > 0 }
    );

    //States
    const [selectedTechnology, setSelectedTechnology] = useState<TechnologyType | null>(null);
    const [selectedZone, setSelectedZone] = useState<{ id: number; name: string } | null>(null);

    //Memos
    const allTechnologies = useMemo(
        () => technologiesQuery.data?.results?.filter((tech: TechnologyType) => tech.displayInCatalog) || [],
        [technologiesQuery.data?.results]
    );

    const catalogZones = useMemo(
        () => catalogZonesQuery.data?.results?.filter((zone) => zone.countryId === country.id) || [],
        [catalogZonesQuery.data?.results, country]
    );

    const technologyBrandImage = useMemo(() => selectedTechnology?.logoImage, [selectedTechnology]);

    const technologies = useMemo(() => {
        return allTechnologies?.filter((technology) => {
            return allProducts?.data?.results?.some((product) =>
                product.technologies.some((t) => t.id === technology.id)
            );
        });
    }, [allTechnologies, allProducts]);

    const availableZonesForTechnology = useMemo(() => {
        return catalogZones.filter((zone) => {
            return allProducts?.data?.results?.some((product) => product.catalogZoneId === zone.id);
        });
    }, [catalogZones, selectedTechnology]);

    const parsedProducts = useMemo(() => {
        const filteredProducts = allProducts?.data?.results?.filter((p) => {
            if (!p.catalogZoneId) {
                return false;
            }
            if (selectedTechnology && !p.technologies.some((t) => t.id === selectedTechnology.id)) {
                return false;
            }

            if (selectedZone && p.catalogZoneId !== selectedZone.id) {
                return false;
            }

            return true;
        });

        return parseProductForProductCard(filteredProducts || [], units);
    }, [allProducts, selectedZone, selectedTechnology]);

    //Effects
    useEffect(() => {
        if (technologies && !selectedTechnology) {
            setSelectedTechnology(technologies[0]);
        }
    }, [technologies, selectedTechnology, availableZonesForTechnology]);

    useEffect(() => {
        setSelectedZone(null);
    }, [selectedTechnology]);

    useEffect(() => {
        if (catalogZones && !selectedZone) {
            setSelectedZone(availableZonesForTechnology[0] ?? catalogZones[0]);
        }
    }, [catalogZones, selectedZone]);

    const refugeOptions = [
        {
            title: _('Refugio estructurado'),
            description: _(
                'Consiste en sembrar el 10% del lote con maíz no Bt, de madurez y fecha de siembra similares al Bt.'
            ),
            images: [
                {
                    title: _('En todas las cabeceras del lote'),
                    image: Structured6,
                },
                {
                    title: _('A través del lote'),
                    image: Structured1,
                },
                {
                    title: _('En otro lote del mismo campo'),
                    image: Structured5,
                },
                {
                    title: _('En la esquina del lote'),
                    image: Structured3,
                },
                {
                    title: _('En una cabecera del lote'),
                    image: Structured2,
                },
                {
                    title: _('En una esquina de un pivot de riego'),
                    image: Structured4,
                },
            ],
        },
        {
            title: _('Refugio en bolsa (RIB)'),
            description: _(
                'Mezcla en la misma bolsa de 90% semillas Bt y 10% no Bt, con híbridos compatibles, para facilitar la siembra y asegurar una distribución uniforme en el lote.'
            ),
            images: [
                {
                    title: null,
                    image: RIB,
                },
            ],
        },
    ];

    return (
        <PageContainer
            header={
                <Header
                    title={_('Biotecnología de nuestros híbridos')}
                    subtitle={_(
                        'Presentamos las mejores tecnologías que protegen tus cultivos, minimizan tus riesgos y maximizan tu rinde.'
                    )}
                    breadcrumbs={[
                        { label: _('Productos'), onClick: () => navigate(reverse('productExploration.main')) },
                        { label: _('Biotecnología') },
                    ]}
                />
            }
        >
            <Stack spacing={2} mt={4}>
                <Stack
                    direction='row'
                    spacing={1}
                    sx={{ overflow: 'scroll', '&::-webkit-scrollbar': { display: 'none' } }}
                >
                    {technologies?.map((technology: TechnologyType) => (
                        <ProductSelectorButton
                            key={technology.id}
                            sx={{ width: 'fit-content', whiteSpace: 'nowrap', fontWeight: 400 }}
                            color={selectedTechnology?.id === technology.id ? 'secondary' : 'primary'}
                            isSelected={selectedTechnology?.id === technology.id}
                            onClick={() => setSelectedTechnology(technology)}
                            label={technology.name}
                            p={{ py: 0.5, px: 1 }}
                        />
                    ))}
                </Stack>
                <Stack direction={'column'} spacing={{ xs: 2, md: 5 }} sx={{ pt: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems='flex-start'>
                        <Stack direction='column' spacing={2} sx={{ width: { xs: '100%', md: '50%', lg: '60%' } }}>
                            <img src={technologyBrandImage} alt={selectedTechnology?.name} style={{ width: 200 }} />
                            <Typography variant='subtitle1' color='primary'>
                                <ReactMarkdown
                                    components={{
                                        p: ({ node, children }) => <>{children}</>, //This removes <p> that wrapps the text, so mui styles are applied correctly
                                    }}
                                >
                                    {_(selectedTechnology?.description)}
                                </ReactMarkdown>
                            </Typography>
                        </Stack>
                        {!!selectedTechnology?.pestsControlled?.length && (
                            <Stack
                                direction='column'
                                spacing={2}
                                p={3}
                                sx={{
                                    width: { xs: '100%', md: '50%', lg: '40%' },
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                    backgroundColor: colors.gray[200],
                                }}
                            >
                                <Typography variant='subtitle1' color='primary' fontWeight={700}>
                                    {_('Plagas que controla')}
                                </Typography>
                                <Stack direction='row' spacing={1} flexWrap='wrap'>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                                            gap: 1,
                                        }}
                                    >
                                        {selectedTechnology?.pestsControlled?.map((pest) => (
                                            <Stack key={pest.id} direction='column' spacing={1} alignItems='center'>
                                                <Avatar
                                                    src={pest.images[0]?.file}
                                                    alt={pest.name}
                                                    sx={{
                                                        width: { xs: 75, lg: 100 },
                                                        height: { xs: 75, lg: 100 },
                                                        border: (t) => `2px solid ${t.palette.primary.main}`,
                                                    }}
                                                />
                                                <Typography variant='subtitle2' color='primary' textAlign='center'>
                                                    {pest.name}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Box>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction='column' spacing={3} sx={{ pt: 2 }}>
                        <Typography variant='h5' color='primary'>
                            {_('Híbridos disponibles con la tecnología')}
                        </Typography>
                        <Stack direction='row' spacing={1}>
                            {catalogZones.map((zone) => (
                                <ProductSelectorButton
                                    key={zone.id}
                                    sx={{ width: 'fit-content', height: 'fit-content', fontWeight: 400 }}
                                    color={selectedZone?.id === zone.id ? 'secondary' : 'primary'}
                                    isSelected={selectedZone?.id === zone.id}
                                    onClick={() => setSelectedZone(zone)}
                                    label={_(zone.name)}
                                    p={{ py: 0.5, px: 1 }}
                                />
                            ))}
                        </Stack>
                        {parsedProducts?.length > 0 ? (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        md: 'repeat(4, 1fr)',
                                        sm: 'repeat(2, 1fr)',
                                        xs: 'repeat(1, 1fr)',
                                    },
                                    gap: 2,
                                }}
                            >
                                {parsedProducts?.map(
                                    ({
                                        catalogName,
                                        name,
                                        id,
                                        chips,
                                        typeLabel,
                                        brand,
                                    }: {
                                        name: string;
                                        catalogName: string;
                                        id: number;
                                        chips: { text: string; sx?: SxProps<Theme> }[];
                                        typeLabel: string;
                                        brand: Brand;
                                    }) => {
                                        return (
                                            <Box key={id} sx={{ border: (t) => `1px solid ${t.palette.divider}` }}>
                                                <ProductCard
                                                    id={id}
                                                    catalogName={catalogName}
                                                    name={name}
                                                    description={typeLabel}
                                                    chips={chips}
                                                    typeLabel={typeLabel}
                                                    sx={{ width: '100%' }}
                                                    catalogType='corn'
                                                    slugLabel={brand.name.toLowerCase()}
                                                />
                                            </Box>
                                        );
                                    }
                                )}
                            </Box>
                        ) : (
                            <Typography variant='body1' color='primary'>
                                {_('No hay productos disponibles para esta tecnología en esta zona')}
                            </Typography>
                        )}
                    </Stack>
                    <Stack direction='column' spacing={3}>
                        <Typography variant='h5' color='primary'>
                            {_('Refugio y cuidado de las tecnologías')}
                        </Typography>
                        <Typography variant='subtitle1' color='primary'>
                            {_(
                                'El refugio ayuda a preservar los cultivos Bt manteniendo insectos susceptibles que reduzcan la aparición de resistencia. Es clave para el Manejo Integrado de Plagas (MIP) y debe complementarse con otras prácticas como la rotación de cultivos, el monitoreo y el uso responsable de insecticidas.'
                            )}
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} pt={1}>
                            {refugeOptions.map((option) => (
                                <Stack
                                    key={option.title}
                                    direction='column'
                                    spacing={2}
                                    sx={{
                                        width: { xs: '100%', md: '50%' },
                                        border: (t) => `1px solid ${t.palette.divider}`,
                                        p: 2,
                                    }}
                                >
                                    <Typography variant='h6' color='primary' fontWeight={700}>
                                        {option.title}
                                    </Typography>
                                    <Typography variant='subtitle1' color='primary'>
                                        {option.description}
                                    </Typography>
                                    <Box
                                        pt={2}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: `repeat(${option.images.length / 3}, 1fr)`,
                                                md: `repeat(${option.images.length / 2}, 1fr)`,
                                            },
                                            gap: 2,
                                        }}
                                    >
                                        {option.images.map((image) => (
                                            <Stack
                                                key={image?.title}
                                                direction='column'
                                                spacing={1}
                                                alignItems='center'
                                            >
                                                <img
                                                    src={image.image}
                                                    alt={image?.title}
                                                    style={{ width: option.images.length === 1 ? 200 : '70%' }}
                                                />
                                                <Typography
                                                    variant='subtitle2'
                                                    color='primary'
                                                    sx={{ width: '70%', textAlign: 'center' }}
                                                >
                                                    {image?.title}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </PageContainer>
    );
};

export default Technology;
